import * as THREE from 'three'
import { webglLogger } from '@/lib/utils/logger'
import { ResourceManager } from './core/ResourceManager'
import { MouseState } from './core/types'
import { webglProfiler } from './core/WebGLProfiler'
import { AdaptiveQualitySystem, analyzeDeviceCapabilities, type QualityLevel, type DeviceCapabilities } from './core/AdaptiveQualitySystem'
import { EnhancedPerformanceMonitor, createEnhancedPerformanceMonitor } from './core/EnhancedPerformanceMonitor'
import { PBRRenderingPipeline, type PBRConfig, type PostProcessingConfig } from './core/PBRRenderingPipeline'
import { HDREnvironmentSystem, type HDREnvironmentConfig } from './core/HDREnvironmentSystem'
import { initializeAccessibility, registerFocusableElement, addElementDescription } from '@/lib/accessibility/initAccessibility'
import type { AccessibilityDescription } from '@/lib/accessibility/AccessibilityManager'

export interface WebGLAppConfig {
  canvas: HTMLCanvasElement
  width?: number
  height?: number
  pixelRatio?: number
  antialias?: boolean
  alpha?: boolean
  debugMode?: boolean
  enableAdaptiveQuality?: boolean
  enablePerformanceMonitoring?: boolean
  initialQuality?: QualityLevel['id']
  enablePBRRendering?: boolean
  enableHDRRendering?: boolean
  pbrConfig?: Partial<PBRConfig>
  postProcessingConfig?: Partial<PostProcessingConfig>
  hdrEnvironmentConfig?: Partial<HDREnvironmentConfig>
}

export abstract class WebGLApp {
  protected renderer: THREE.WebGLRenderer
  protected scene: THREE.Scene
  protected camera: THREE.PerspectiveCamera | THREE.OrthographicCamera
  protected clock: THREE.Clock
  protected width: number
  protected height: number
  protected animationId: number | null = null
  protected isRunning = false
  public isInitialized = false
  private isDisposed = false
  
  // Shared mouse interaction state (updated by common InteractionManager)
  protected mouseState: MouseState = {
    x: 0,
    y: 0,
    normalizedX: 0,
    normalizedY: 0,
    isPressed: false
  }
  
  // Automatic resource management
  protected resourceManager: ResourceManager
  
  // Adaptive quality and performance monitoring
  protected adaptiveQuality: AdaptiveQualitySystem | null = null
  protected performanceMonitor: EnhancedPerformanceMonitor | null = null
  protected deviceCapabilities: DeviceCapabilities | null = null
  private enableAdaptiveQuality: boolean
  private enablePerformanceMonitoring: boolean

  // PBR rendering pipeline
  protected pbrPipeline: PBRRenderingPipeline | null = null
  protected hdrEnvironment: HDREnvironmentSystem | null = null
  private enablePBRRendering: boolean
  private enableHDRRendering: boolean

  constructor(config: WebGLAppConfig) {
    const {
      canvas,
      width = window.innerWidth,
      height = window.innerHeight,
      pixelRatio = window.devicePixelRatio || 1,
      antialias = true,
      alpha = false,
      debugMode = process.env.NODE_ENV === 'development',
      enableAdaptiveQuality = true,
      enablePerformanceMonitoring = true,
      initialQuality,
      enablePBRRendering = false,
      enableHDRRendering = false,
      pbrConfig,
      postProcessingConfig,
      hdrEnvironmentConfig
    } = config

    this.width = width
    this.height = height
    this.enableAdaptiveQuality = enableAdaptiveQuality
    this.enablePerformanceMonitoring = enablePerformanceMonitoring
    this.enablePBRRendering = enablePBRRendering
    this.enableHDRRendering = enableHDRRendering

    // Initialize resource manager
    this.resourceManager = new ResourceManager(debugMode)

    // Initialize device capabilities asynchronously
    this.initializeAdaptiveSystems(initialQuality)
    
    // Initialize renderer first
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias,
      alpha,
      powerPreference: 'high-performance'
    })
    this.renderer.setSize(width, height)
    this.renderer.setPixelRatio(Math.min(pixelRatio, 2))
    
    // Set accessibility attributes for canvas
    canvas.setAttribute('role', 'application')
    canvas.setAttribute('aria-label', 'WebGL 3D visualization canvas')
    canvas.setAttribute('tabindex', '0')
    canvas.id = 'main-canvas'
    
    // Initialize scene and camera
    this.scene = new THREE.Scene()
    this.camera = this.createCamera()
    this.clock = new THREE.Clock()
    
    // Initialize PBR and HDR systems
    this.initializePBRSystems(pbrConfig, postProcessingConfig, hdrEnvironmentConfig)

    // Setup WebGL context loss handling
    this.setupContextLossHandling(canvas)
    
    // Initialize accessibility features
    initializeAccessibility()

    // Defer init until start() so subclasses can finish their own constructor
  }

  /**
   * Register a focusable element for accessibility
   */
  protected registerFocusableElement(
    id: string,
    element: HTMLElement,
    description?: string
  ): void {
    registerFocusableElement(id, element, description)
  }

  /**
   * Add element description for accessibility
   */
  protected addElementDescription(description: AccessibilityDescription): void {
    addElementDescription(description)
  }

  /**
   * Initialize adaptive quality and performance monitoring systems
   */
  private async initializeAdaptiveSystems(initialQuality?: QualityLevel['id']): Promise<void> {
    try {
      // Analyze device capabilities
      this.deviceCapabilities = await analyzeDeviceCapabilities()

      // Bail out if the instance was disposed while awaiting async device analysis.
      // Without this guard a fire-and-forget init could re-attach systems to a
      // disposed instance and reference a disposed renderer (zombie revival).
      if (this.isDisposed) return

      // Initialize adaptive quality system
      if (this.enableAdaptiveQuality) {
        this.adaptiveQuality = new AdaptiveQualitySystem(
          this.deviceCapabilities,
          initialQuality
        )
        
        // Listen for quality changes
        this.adaptiveQuality.onQualityChange('webgl-app', (quality) => {
          this.onQualityChanged(quality)
        })
      }
      
      // Initialize performance monitor
      if (this.enablePerformanceMonitoring) {
        this.performanceMonitor = createEnhancedPerformanceMonitor(
          this.renderer,
          this.deviceCapabilities
        )
        
        // Connect performance monitor to adaptive quality
        if (this.adaptiveQuality) {
          this.performanceMonitor.onMetricsUpdate('adaptive-quality', (metrics) => {
            this.adaptiveQuality!.updatePerformanceMetrics(metrics)
          })
        }
        
        // Listen for performance alerts
        this.performanceMonitor.onAlert('webgl-app', (alert) => {
          this.onPerformanceAlert(alert)
        })
      }
      
      webglLogger.info('[WebGLApp] Adaptive systems initialized', {
        adaptiveQuality: !!this.adaptiveQuality,
        performanceMonitoring: !!this.performanceMonitor,
        deviceCapabilities: this.deviceCapabilities
      })
    } catch (error) {
      webglLogger.error('[WebGLApp] Failed to initialize adaptive systems:', error)
    }
  }

  /**
   * Initialize PBR and HDR rendering systems
   */
  private initializePBRSystems(
    pbrConfig?: Partial<PBRConfig>,
    postProcessingConfig?: Partial<PostProcessingConfig>,
    hdrEnvironmentConfig?: Partial<HDREnvironmentConfig>
  ): void {
    try {
      // Initialize PBR rendering pipeline
      if (this.enablePBRRendering) {
        this.pbrPipeline = new PBRRenderingPipeline(
          this.renderer,
          this.scene,
          this.camera,
          pbrConfig,
          postProcessingConfig
        )
            
        webglLogger.info('[WebGLApp] PBR rendering pipeline initialized')
      }
          
      // Initialize HDR environment system
      if (this.enableHDRRendering) {
        this.hdrEnvironment = new HDREnvironmentSystem(
          this.renderer,
          this.scene,
          hdrEnvironmentConfig
        )
            
        webglLogger.info('[WebGLApp] HDR environment system initialized')
      }
    } catch (error) {
      webglLogger.error('[WebGLApp] Failed to initialize PBR/HDR systems:', error)
    }
  }

  private setupContextLossHandling(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('webglcontextlost', (event: Event) => {
      event.preventDefault()
      this.stop()
      webglLogger.warn('WebGL context lost')
      
      // Notify external handlers
      this.onContextLost?.()
    })

    canvas.addEventListener('webglcontextrestored', () => {
      webglLogger.info('WebGL context restored')
      
      try {
        // Reinitialize renderer and resources
        this.handleContextRestore()
        this.init()
        this.start()
        
        // Notify external handlers
        this.onContextRestored?.()
      } catch (error) {
        webglLogger.error('Failed to restore WebGL context:', error)
        this.onContextRestoreError?.(error instanceof Error ? error : new Error(String(error)))
      }
    })
  }

  // Context loss event handlers (can be overridden by subclasses)
  protected onContextLost?(): void
  protected onContextRestored?(): void
  protected onContextRestoreError?(error: Error): void

  // Handle context restoration - can be overridden by subclasses
  protected handleContextRestore(): void {
    // Default implementation - recreate basic renderer state
    // Subclasses should override this to restore their specific resources
  }

  protected abstract createCamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera
  protected abstract init(): void
  protected abstract update(deltaTime: number, elapsedTime: number): void

  public resize(width: number, height: number): void {
    this.width = width
    this.height = height

    if (this.camera instanceof THREE.PerspectiveCamera) {
      this.camera.aspect = width / height
      this.camera.updateProjectionMatrix()
    } else if (this.camera instanceof THREE.OrthographicCamera) {
      const aspect = width / height
      const frustumSize = 10
      this.camera.left = -frustumSize * aspect / 2
      this.camera.right = frustumSize * aspect / 2
      this.camera.top = frustumSize / 2
      this.camera.bottom = -frustumSize / 2
      this.camera.updateProjectionMatrix()
    }

    this.renderer.setSize(width, height, false)
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))
    
    // Resize PBR pipeline
    if (this.pbrPipeline) {
      this.pbrPipeline.resize(width, height)
    }
    
    // Update accessibility features
    this.updateAccessibilityForResize(width, height)
  }

  /**
   * Update accessibility features when resizing
   */
  private updateAccessibilityForResize(width: number, height: number): void {
    // Notify screen readers of canvas size change
    const canvas = this.renderer.domElement
    const sizeDescription = `Canvas size: ${width} by ${height} pixels`
    canvas.setAttribute('aria-label', `WebGL 3D visualization canvas. ${sizeDescription}`)
    
    // In a real implementation, you might want to announce this to screen readers
    // For now, we'll just update the ARIA attributes
  }

  public start(): void {
    if (this.isRunning) {
      webglLogger.debug('WebGLApp already running, skipping start');
      return
    }
    if (!this.isInitialized) {
      webglLogger.debug('WebGLApp initializing...');
      this.init()
      this.isInitialized = true
    }
    this.isRunning = true
    this.clock.start()
    
    // Start performance monitoring
    if (this.performanceMonitor) {
      this.performanceMonitor.start()
    }
    
    this.animate()
    webglLogger.debug('WebGLApp started');
  }

  public stop(): void {
    this.isRunning = false
    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
    this.clock.stop()
    
    // Stop performance monitoring
    if (this.performanceMonitor) {
      this.performanceMonitor.stop()
    }
  }

  private animate = (): void => {
    if (!this.isRunning) {
      webglLogger.debug('Animation loop stopped - isRunning is false');
      return
    }

    this.animationId = requestAnimationFrame(this.animate)

    const frameStart = webglProfiler.startFrame();
    const deltaTime = this.clock.getDelta()
    const elapsedTime = this.clock.getElapsedTime()

    try {
      const updateStart = performance.now();
      this.update(deltaTime, elapsedTime)
      const updateTime = performance.now() - updateStart;
      
      // Update HDR environment animation
      if (this.hdrEnvironment) {
        this.hdrEnvironment.updateAnimation(deltaTime)
      }
      
      // Render with PBR pipeline or standard rendering
      if (this.pbrPipeline) {
        this.pbrPipeline.render()
      } else {
        this.renderer.render(this.scene, this.camera)
      }
      
      // Update performance monitoring
      if (this.performanceMonitor) {
        this.performanceMonitor.update()
      }
      
      // Profile performance
      webglProfiler.endFrame(frameStart, this.renderer, updateTime);
    } catch (error) {
      webglLogger.error('Error in animation loop:', error);
      this.stop();
    }
  }

  /**
   * Update mouse interaction state from the common interaction manager.
   * Subclasses can override onMouseStateUpdate to react immediately.
   */
  public updateMouseState(state: MouseState): void {
    this.mouseState = state
    this.onMouseStateUpdate(state)
  }

  /**
   * Called when adaptive quality level changes
   */
  protected onQualityChanged(quality: QualityLevel): void {
    const settings = quality.settings
    
    // Apply resolution scaling
    this.renderer.setPixelRatio(
      Math.min(window.devicePixelRatio * settings.resolutionScale, settings.pixelRatioLimit)
    )
    
    // Apply shadow map size
    this.scene.traverse((object) => {
      if (object instanceof THREE.DirectionalLight || object instanceof THREE.SpotLight || object instanceof THREE.PointLight) {
        if (object.shadow) {
          object.shadow.mapSize.setScalar(settings.shadowMapSize)
          object.shadow.map?.dispose()
          object.shadow.map = null
        }
      }
    })
    
    // Apply texture filtering
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh && object.material) {
        const materials = Array.isArray(object.material) ? object.material : [object.material]
        materials.forEach((material) => {
          if (material instanceof THREE.Material) {
            // Update texture filtering for all textures in the material
            Object.values(material).forEach((value: unknown) => {
              if (value instanceof THREE.Texture) {
                // TextureFilter型の互換性を確保
                value.magFilter = settings.textureFiltering as THREE.MagnificationTextureFilter;
                value.minFilter = settings.textureFiltering as THREE.MinificationTextureFilter;
                value.needsUpdate = true
              }
            })
          }
        })
      }
    })
    
    webglLogger.info('[WebGLApp] Quality settings applied', {
      quality: quality.id,
      settings
    })
  }

  /**
   * Called when performance alert is triggered
   */
  protected onPerformanceAlert(alert: { message: string }): void {
    webglLogger.warn('[WebGLApp] Performance alert:', alert.message)
    // Subclasses can override to handle specific alerts
  }

  /**
   * Get current adaptive quality system
   */
  public getAdaptiveQuality(): AdaptiveQualitySystem | null {
    return this.adaptiveQuality
  }

  /**
   * Get current performance monitor
   */
  public getPerformanceMonitor(): EnhancedPerformanceMonitor | null {
    return this.performanceMonitor
  }

  /**
   * Get device capabilities
   */
  public getDeviceCapabilities(): DeviceCapabilities | null {
    return this.deviceCapabilities
  }

  /**
   * Get PBR rendering pipeline
   */
  public getPBRPipeline(): PBRRenderingPipeline | null {
    return this.pbrPipeline
  }

  /**
   * Get HDR environment system
   */
  public getHDREnvironment(): HDREnvironmentSystem | null {
    return this.hdrEnvironment
  }

  /**
   * Enable/disable PBR rendering
   */
  public setPBRRendering(enabled: boolean): void {
    if (enabled && !this.pbrPipeline) {
      this.pbrPipeline = new PBRRenderingPipeline(
        this.renderer,
        this.scene,
        this.camera
      )
      this.enablePBRRendering = true
      webglLogger.info('[WebGLApp] PBR rendering enabled')
    } else if (!enabled && this.pbrPipeline) {
      this.pbrPipeline.dispose()
      this.pbrPipeline = null
      this.enablePBRRendering = false
      webglLogger.info('[WebGLApp] PBR rendering disabled')
    }
  }

  /**
   * Enable/disable HDR environment
   */
  public setHDREnvironment(enabled: boolean): void {
    if (enabled && !this.hdrEnvironment) {
      this.hdrEnvironment = new HDREnvironmentSystem(
        this.renderer,
        this.scene
      )
      this.enableHDRRendering = true
      webglLogger.info('[WebGLApp] HDR environment enabled')
    } else if (!enabled && this.hdrEnvironment) {
      this.hdrEnvironment.dispose()
      this.hdrEnvironment = null
      this.enableHDRRendering = false
      webglLogger.info('[WebGLApp] HDR environment disabled')
    }
  }

  /**
   * Load HDR environment map
   */
  public async loadEnvironmentMap(url: string): Promise<void> {
    if (!this.hdrEnvironment) {
      this.setHDREnvironment(true)
    }
    
    if (this.hdrEnvironment) {
      await this.hdrEnvironment.loadEnvironmentMap(url)
    }
  }

  // Hook for subclasses; default no-op
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onMouseStateUpdate(state: MouseState): void {}

  /**
   * Precompile shaders/programs for current scene/camera to avoid first-frame hitch
   */
  public precompile(): void {
    try {
      // Schedule on idle if possible to avoid blocking first paint
      const run = () => {
        try {
          this.renderer.compile(this.scene, this.camera)
        } catch {
          // no-op; compile is best-effort
        }
      }
      // @ts-expect-error requestIdleCallback may not exist in all browsers
      const ric: ((cb: () => void) => number) | undefined = typeof window !== 'undefined' && window.requestIdleCallback
      if (ric) {
        ric(run)
      } else {
        setTimeout(run, 0)
      }
    } catch {
      // ignore
    }
  }

  // Performance monitoring support
  // Performance monitor is already declared above
  // protected performanceMonitor?: { dispose?: () => void };
  
  public setPerformanceMonitor(monitor: EnhancedPerformanceMonitor) {
    this.performanceMonitor = monitor;
  }

  // Helper methods for resource tracking
  protected trackGeometry(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
    return this.resourceManager.trackGeometry(geometry);
  }

  protected trackMaterial(material: THREE.Material): THREE.Material {
    return this.resourceManager.trackMaterial(material);
  }

  protected trackTexture(texture: THREE.Texture): THREE.Texture {
    return this.resourceManager.trackTexture(texture);
  }

  protected trackDisposable(resource: { dispose(): void }): { dispose(): void } {
    return this.resourceManager.trackDisposable(resource);
  }

  // Get resource statistics for performance monitoring
  protected getResourceStats(): { geometries: number; textures: number; materials: number } {
    return this.resourceManager.getStats();
  }

  public dispose(): void {
    // Idempotent dispose: second and subsequent calls are no-ops.
    // Setting the flag first also makes any in-flight initializeAdaptiveSystems
    // bail out at its post-await guard instead of reviving this instance.
    if (this.isDisposed) return;
    this.isDisposed = true;

    this.stop();

    // Dispose adaptive systems
    if (this.adaptiveQuality) {
      this.adaptiveQuality.dispose();
      this.adaptiveQuality = null;
    }
    
    // Dispose PBR and HDR systems
    if (this.pbrPipeline) {
      this.pbrPipeline.dispose();
      this.pbrPipeline = null;
    }
    
    if (this.hdrEnvironment) {
      this.hdrEnvironment.dispose();
      this.hdrEnvironment = null;
    }
    
    // Dispose performance monitor
    if (this.performanceMonitor && 'dispose' in this.performanceMonitor && typeof this.performanceMonitor.dispose === 'function') {
      this.performanceMonitor.dispose();
      this.performanceMonitor = null;
    }
    
    // Dispose all tracked resources automatically
    this.resourceManager.dispose();
    
    // Clear the scene (resources already disposed by ResourceManager)
    this.scene.clear();
    
    // Dispose renderer last
    this.renderer.dispose();
    
    webglLogger.info('[WebGLApp] Disposed with all systems');
  }

  public getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  public getScene(): THREE.Scene {
    return this.scene;
  }

  public getCamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera {
    return this.camera;
  }
}